// Shared atoms used across ProductDetail subcomponents.
import {
    Activity,
    CheckCircle2,
    Circle,
    Clock,
    PauseCircle,
    XCircle,
    Power,
    ChevronRight,
    FileEdit,
    X,
} from "lucide-react";

export function GateStatusPill({ status }) {
    const map = {
        "Not Started": { cls: "border-[#2A2A2A] text-snow-muted", icon: Circle },
        "In Progress": { cls: "border-signal text-signal bg-signal/5", icon: Clock },
        "In Review": { cls: "border-signal text-signal bg-signal/10 animate-pulse-signal", icon: Clock },
        "Passed": { cls: "border-snow text-snow bg-snow/5", icon: CheckCircle2 },
        "Failed": { cls: "border-red-500/50 text-red-400", icon: XCircle },
        "On Hold": { cls: "border-yellow-500/50 text-yellow-400", icon: PauseCircle },
    };
    const cfg = map[status] || map["Not Started"];
    const Icon = cfg.icon;
    return (
        <span
            data-testid={`gate-status-pill-${(status || "").replace(/\s+/g, "-").toLowerCase()}`}
            className={`inline-flex items-center gap-1.5 border px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-mono ${cfg.cls}`}
        >
            <Icon className="w-3 h-3" />
            {status}
        </span>
    );
}

export function ActivityIcon({ type }) {
    const map = {
        "Kill-Switch Engage": <Power className="w-4 h-4 text-signal" />,
        "Kill-Switch Release": <Power className="w-4 h-4 text-green-400" />,
        "Status Change": <ChevronRight className="w-4 h-4 text-yellow-400" />,
        "Template Edit": <FileEdit className="w-4 h-4 text-snow-dim" />,
    };
    return (
        <div className="w-7 h-7 border border-[#2A2A2A] flex items-center justify-center flex-shrink-0">
            {map[type] || <Activity className="w-4 h-4 text-snow-dim" />}
        </div>
    );
}

export function ActionBadge({ type }) {
    const cls = type.startsWith("Kill-Switch")
        ? "border-signal/60 text-signal bg-signal/5"
        : type === "Status Change"
        ? "border-yellow-500/40 text-yellow-400"
        : "border-[#2A2A2A] text-snow-dim";
    return (
        <span className={`inline-flex items-center border px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-mono ${cls}`}>
            {type}
        </span>
    );
}

export function formatTime(iso) {
    try {
        const d = new Date(iso);
        const now = new Date();
        const diff = (now - d) / 1000;
        if (diff < 60) return `${Math.floor(diff)}s ago`;
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return d.toISOString().slice(0, 16).replace("T", " ") + " UTC";
    } catch {
        return iso;
    }
}

export function Modal({ title, children, onClose, accent }) {
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-jet/80 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <div
                className={`relative w-full max-w-xl bg-jet-elev1 border ${accent ? "border-signal" : "border-hairline"} max-h-[90vh] overflow-y-auto`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={`flex items-center justify-between border-b ${accent ? "border-signal" : "border-hairline"} px-5 py-3`}>
                    <div>
                        <div className="overline">Action</div>
                        <div className="heading-display text-lg">{title}</div>
                    </div>
                    <button onClick={onClose} className="text-snow-dim hover:text-signal" data-testid="modal-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-5">{children}</div>
            </div>
        </div>
    );
}
